import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Popup, Marker } from 'react-leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FaSmile, FaGlobe, FaFlag, FaMousePointer, FaUsers } from 'react-icons/fa';

// Fix for default markers in react-leaflet
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create custom profile icon (smiling face)
const createProfileIcon = (size = 30) => {
  return new Icon({
    iconUrl: `data:image/svg+xml;base64,${btoa(`
      <svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="45" fill="#ffde04" stroke="#000" stroke-width="3"/>
        <circle cx="35" cy="40" r="5" fill="#000"/>
        <circle cx="65" cy="40" r="5" fill="#000"/>
        <path d="M 30 65 Q 50 80 70 65" stroke="#000" stroke-width="4" fill="none" stroke-linecap="round"/>
      </svg>
    `)}`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

// Create custom clicks icon (target/pointer)
const createClickIcon = (size = 30) => {
  return new Icon({
    iconUrl: `data:image/svg+xml;base64,${btoa(`
      <svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="45" fill="#2196F3" stroke="#1976D2" stroke-width="3"/>
        <path d="M 50 25 L 50 75 M 25 50 L 75 50" stroke="#FFF" stroke-width="6" stroke-linecap="round"/>
        <circle cx="50" cy="50" r="15" fill="#FFF"/>
        <circle cx="50" cy="50" r="7" fill="#2196F3"/>
      </svg>
    `)}`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, icon: IconComponent, isVisible }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (!isVisible || hasAnimated) return;

    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * end);

      setCount(currentCount);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setHasAnimated(true);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [end, duration, isVisible, hasAnimated]);

  return (
    <div className="animated-counter">
      {IconComponent && <IconComponent className="counter-icon" />}
      <span className="counter-number">{count.toLocaleString()}</span>
    </div>
  );
};

const WorldMap = () => {
  const [mapData, setMapData] = useState({
    profiles: [],
    clicks: []
  });
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    // Focus on India, USA, and Philippines
    const profileData = [
      { lat: 28.6139, lng: 77.2090, country: 'India', count: 16245 },
      { lat: 40.7128, lng: -74.0060, country: 'United States', count: 13210 },
      { lat: 14.5995, lng: 120.9842, country: 'Philippines', count: 4215 },
      { lat: 51.5074, lng: -0.1278, country: 'United Kingdom', count: 8960 },
      { lat: -33.8688, lng: 151.2093, country: 'Australia', count: 5342 }
    ];

    const clickData = [
      { lat: 29.6139, lng: 77.2090, country: 'India', clicks: 28410 },
      { lat: 40.7128, lng: -74.0060, country: 'United States', clicks: 19234 },
      { lat: 14.5995, lng: 120.9842, country: 'Philippines', clicks: 8345 },
      { lat: 51.5074, lng: -0.1278, country: 'United Kingdom', clicks: 10456 },
      { lat: -33.8688, lng: 151.2093, country: 'Australia', clicks: 6540 }
    ];

    setMapData({
      profiles: profileData,
      clicks: clickData
    });
  }, []);

  // Intersection Observer for animation trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of the section is visible
        rootMargin: '0px 0px -50px 0px' // Start animation slightly before fully visible
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const getMarkerSize = (count, type) => {
    if (type === 'profile') {
      return Math.max(8, Math.min(25, count / 200));
    } else {
      return Math.max(8, Math.min(30, count / 1000));
    }
  };

  return (
    <section className="worldmap-section" ref={sectionRef}>
      <div className="container">
        <h2 data-aos="flip-down">Our Global Reach</h2>
        <p className="map-description" data-aos="slide-up" data-aos-delay="200">
          See our presence in India, USA, and Philippines - where our users are located and where your links are being clicked
        </p>

        <div className="map-container" data-aos="zoom-in" data-aos-delay="400">
          <MapContainer
            center={[20, 0]} // Center the map to show the whole world
            zoom={2} // Zoom out to show more of the world
            style={{ height: '500px', width: '100%' }}
            className="world-map"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {/* Profile Markers */}
            {mapData.profiles.map((profile, index) => (
              <Marker
                key={`profile-${index}`}
                position={[profile.lat, profile.lng]}
                icon={createProfileIcon(getMarkerSize(profile.count, 'profile') * 2)}
              >
                <Popup>
                  <div className="popup-content">
                    <h4><FaSmile className="popup-icon" /> Digital Profiles</h4>
                    <p><strong>{profile.country}</strong></p>
                    <p>Profiles: {profile.count.toLocaleString()}</p>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Click Markers */}
            {mapData.clicks.map((click, index) => (
              <Marker
                key={`click-${index}`}
                position={[click.lat, click.lng]}
                icon={createClickIcon(getMarkerSize(click.clicks, 'click') * 2)}
              >
                <Popup>
                  <div className="popup-content">
                    <h4><FaMousePointer className="popup-icon" /> Link Clicks</h4>
                    <p><strong>{click.country}</strong></p>
                    <p>Clicks: {click.clicks.toLocaleString()}</p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div className="map-legend" data-aos="fade-up" data-aos-delay="600">
          <div className="legend-item" data-aos="flip-left" data-aos-delay="700">
            <div className="legend-icon profile-icon">
              <svg width="24" height="24" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" fill="#ffde04" stroke="#000" stroke-width="3" />
                <circle cx="35" cy="40" r="5" fill="#000" />
                <circle cx="65" cy="40" r="5" fill="#000" />
                <path d="M 30 65 Q 50 80 70 65" stroke="#000" stroke-width="4" fill="none" stroke-linecap="round" />
              </svg>
            </div>
            <span>Digital Profiles (Users)</span>
          </div>
          <div className="legend-item" data-aos="flip-right" data-aos-delay="800">
            <div className="legend-icon click-icon">
              <svg width="24" height="24" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" fill="#2196F3" stroke="#1976D2" stroke-width="3" />
                <path d="M 50 25 L 50 75 M 25 50 L 75 50" stroke="#FFF" stroke-width="6" stroke-linecap="round" />
                <circle cx="50" cy="50" r="15" fill="#FFF" />
                <circle cx="50" cy="50" r="7" fill="#2196F3" />
              </svg>
            </div>
            <span>Link Clicks (Activity)</span>
          </div>
        </div>

        {/* <div className="map-stats" data-aos="slide-up" data-aos-delay="900">
          <div className="map-stat" data-aos="bounce-in" data-aos-delay="1000">
            <AnimatedCounter 
              end={mapData.profiles.length} 
              duration={1500}
              icon={FaFlag}
              isVisible={isVisible}
            />
            <p>Countries Served</p>
          </div>
          <div className="map-stat" data-aos="bounce-in" data-aos-delay="1100">
            <AnimatedCounter 
              end={mapData.clicks.reduce((sum, click) => sum + click.clicks, 0)} 
              duration={2500}
              icon={FaMousePointer}
              isVisible={isVisible}
            />
            <p>Total Clicks</p>
          </div>
          <div className="map-stat" data-aos="bounce-in" data-aos-delay="1200">
            <AnimatedCounter 
              end={mapData.profiles.reduce((sum, profile) => sum + profile.count, 0)} 
              duration={2000}
              icon={FaUsers}
              isVisible={isVisible}
            />
            <p>Total Profiles</p>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default WorldMap;
